import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useDataVaultStore, type CrossFilterState } from '@/store/dataVault';

// ─── Types (re-exported for backward compatibility) ──────────────────
export type { CrossFilterState };

interface CrossFilterContextValue {
  filter: CrossFilterState;
  highlight: (filter: CrossFilterState) => void;
  clear: () => void;
  isHighlighted: (well?: string, date?: string, analyte?: string) => boolean;
  isActive: boolean;
  getOpacity: (well?: string, date?: string, analyte?: string) => number;
}

const CrossFilterCtx = createContext<CrossFilterContextValue | null>(null);

// ─── Provider (thin adapter delegating to Zustand store) ─────────────

export function CrossFilterProvider({ children }: { children: ReactNode }) {
  const crossFilter = useDataVaultStore((s) => s.crossFilter);
  const highlightCrossFilter = useDataVaultStore((s) => s.highlightCrossFilter);
  const clearCrossFilter = useDataVaultStore((s) => s.clearCrossFilter);

  const isActive = !!(crossFilter.well || crossFilter.date || crossFilter.analyte);

  const isHighlighted = useCallback(
    (well?: string, date?: string, analyte?: string) => {
      if (!isActive) return true;
      if (crossFilter.well && well && crossFilter.well !== well) return false;
      if (crossFilter.date && date && crossFilter.date !== date) return false;
      if (crossFilter.analyte && analyte && crossFilter.analyte !== analyte) return false;
      return true;
    },
    [crossFilter, isActive],
  );

  const getOpacity = useCallback(
    (well?: string, date?: string, analyte?: string) => {
      if (!isActive) return 1;
      return isHighlighted(well, date, analyte) ? 1 : 0.15;
    },
    [isActive, isHighlighted],
  );

  return (
    <CrossFilterCtx.Provider
      value={{
        filter: crossFilter,
        highlight: highlightCrossFilter,
        clear: clearCrossFilter,
        isHighlighted,
        isActive,
        getOpacity,
      }}
    >
      {children}
    </CrossFilterCtx.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────────

export function useCrossFilter() {
  const ctx = useContext(CrossFilterCtx);
  if (!ctx) {
    // Return no-op defaults when used outside provider (graceful degradation)
    return {
      filter: {} as CrossFilterState,
      highlight: () => {},
      clear: () => {},
      isHighlighted: () => true,
      isActive: false,
      getOpacity: () => 1,
    };
  }
  return ctx;
}
